/**
 * Provenance marker + legend for the E2 SiteHeader (PROD-10693). Each marker
 * is an icon AND a word — never colour alone — so the label reads in
 * greyscale and to a screen reader.
 */
import type React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AutoAwesomeRounded,
  CheckCircleRounded,
  RemoveCircleOutlineRounded,
} from "@mui/icons-material"
import { Box, Button, Popover, Stack, Tooltip, Typography } from "@mui/material"

import type { Provenance } from "./schedule-misalignment.types"

const PROVENANCES: Provenance[] = [
  "confirmed",
  "estimated",
  "inferred",
  "missing",
]

const PROVENANCE_COLOR: Record<Provenance, string> = {
  confirmed: "success.main",
  estimated: "text.secondary",
  inferred: "info.main",
  missing: "warning.dark",
}

const ICON_SX = { fontSize: 14 }

const Icon: React.FC<{ provenance: Provenance }> = ({ provenance }) => {
  switch (provenance) {
    case "confirmed":
      return <CheckCircleRounded aria-hidden sx={ICON_SX} />
    case "inferred":
      return <AutoAwesomeRounded aria-hidden sx={ICON_SX} />
    case "missing":
      return <RemoveCircleOutlineRounded aria-hidden sx={ICON_SX} />
    default:
      return (
        <Box
          aria-hidden
          component="span"
          sx={{ fontWeight: 700, lineHeight: 1 }}
        >
          ~
        </Box>
      )
  }
}

export const ProvenanceMarker: React.FC<{
  provenance: Provenance
  /** Extra source copy, already translated, shown after the legend text. */
  detail?: string
}> = ({ provenance, detail }) => {
  const { t } = useTranslation("common")
  const word = t(`neat.provenance.label.${provenance}`)
  const marker = (
    <Stack
      component="span"
      direction="row"
      spacing={0.5}
      sx={{ alignItems: "center", color: PROVENANCE_COLOR[provenance] }}
      // Focusable so the detail tooltip opens from the keyboard. The group
      // role and label keep the visible word as the accessible name; the
      // tooltip copy is its description (describeChild).
      {...(detail ? { tabIndex: 0, role: "group", "aria-label": word } : {})}
    >
      <Icon provenance={provenance} />
      <Typography component="span" sx={{ fontWeight: 600 }} variant="caption">
        {word}
      </Typography>
    </Stack>
  )
  return detail ? (
    <Tooltip
      describeChild
      title={`${t(`neat.provenance.legend.${provenance}`)} ${detail}`}
    >
      {marker}
    </Tooltip>
  ) : (
    marker
  )
}

export const ProvenanceLegend: React.FC = () => {
  const { t } = useTranslation("common")
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  return (
    <>
      <Button
        onClick={(e) => {
          setAnchor(e.currentTarget)
        }}
        size="small"
        sx={{ textTransform: "none", p: 0, minWidth: 0 }}
        variant="text"
      >
        {t("neat.provenance.legendButton")}
      </Button>
      <Popover
        anchorEl={anchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => {
          setAnchor(null)
        }}
        open={anchor !== null}
      >
        <Stack spacing={1.25} sx={{ p: 2, maxWidth: 360 }}>
          {PROVENANCES.map((p) => (
            <Stack direction="row" key={p} spacing={1.5}>
              <Box sx={{ minWidth: 92 }}>
                <ProvenanceMarker provenance={p} />
              </Box>
              <Typography variant="body2">
                {t(`neat.provenance.legend.${p}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  )
}
